// @flow
import { Record } from 'immutable';
import { ModerationState } from 'models';
import isToday from 'date-fns/is_today';
import { type ActionType, handleActions } from 'redux-actions';
import {
  requestContent,
  receiveContent,
  updateCurrentUgcId,
  requestPreviousUgcIdUpdate,
  receivePreviousUgcIdUpdate,
} from './moderateActions';
import { commitObservationsUpdate } from 'ducks/coreDataActions';
import ModerateStorage from 'pages/moderation/moderate/moderateStorage';

type SavedCompletedCountRecord = {
  date?: number,
  count?: number,
};
type ModerateRecordShape = {
  savedCompletedCount: SavedCompletedCountRecord,
  completedCount: number,
  ugcId: ?UgcId,
  contentRequestStatus: RequestStatus,
  previousUgcId: ?UgcId,
  updatePreviousContentRequestStatus: RequestStatus,
};

const defaultModerateRecordShape: ModerateRecordShape = {
  savedCompletedCount: {},
  completedCount: 0,
  ugcId: null,
  contentRequestStatus: null,
  previousUgcId: null,
  updatePreviousContentRequestStatus: null,
};

export class ModerateState extends Record(defaultModerateRecordShape) {
  getSavedCount() {
    const { date, count: savedCount = 0 } = this.savedCompletedCount;

    return isToday(date) ? savedCount : 0;
  }

  getCompletedCount() {
    return this.getSavedCount() + this.completedCount;
  }

  static createWithCompletedCount(obj: SavedCompletedCountRecord) {
    return new ModerateState({
      savedCompletedCount: obj,
    });
  }
}

const defaultState = ModerateState.createWithCompletedCount(
  ModerateStorage.getTodayCount()
);

export default handleActions(
  {
    [requestContent.toString()]: (
      state: ModerateState,
      action: ActionType<typeof requestContent>
    ) => state.set('contentRequestStatus', 'pending'),

    [receiveContent.toString()]: {
      next(state: ModerateState, action: ActionType<typeof receiveContent>) {
        return state.merge({
          ugcId: action.payload.ugc_id,
          contentRequestStatus: 'complete',
        });
      },
      throw(state: ModerateState, action: ActionType<typeof receiveContent>) {
        return state.set('contentRequestStatus', action.payload);
      },
    },

    [updateCurrentUgcId.toString()]: (
      state: ModerateState,
      action: ActionType<typeof updateCurrentUgcId>
    ) => state.set('ugcId', action.payload),

    [requestPreviousUgcIdUpdate.toString()]: (
      state: ModerateState,
      action: ActionType<typeof requestPreviousUgcIdUpdate>
    ) => state.set('updatePreviousContentRequestStatus', 'pending'),

    [receivePreviousUgcIdUpdate.toString()]: (
      state: ModerateState,
      action: ActionType<typeof receivePreviousUgcIdUpdate>
    ) => {
      return action.payload !== state.previousUgcId
        ? state.merge({
            previousUgcId: action.payload,
            updatePreviousContentRequestStatus: 'complete',
          })
        : state;
    },

    [commitObservationsUpdate.toString()]: (
      state: ModerateState,
      action: ActionType<typeof commitObservationsUpdate>
    ) => {
      const committedModerationState = action.payload;
      const completedCount =
        ModerationState.isPowerReviewsModerated(committedModerationState) &&
        action.payload.ugcId !== state.previousUgcId
          ? state.completedCount + 1
          : state.completedCount;

      return state.merge({
        completedCount,
      });
    },
  },
  defaultState
);



// WEBPACK FOOTER //
// ./src/pages/moderation/moderate/moderateReducer.js